// views/Profile/components/PackageCard.jsx
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Coins, Star, Loader2 } from 'lucide-react';
import './PackageCard.css';

const formatCoins = (value) => {
  if (value == null || Number.isNaN(value)) return '—';
  return new Intl.NumberFormat('en-US').format(value);
};

const formatUsd = (value) => {
  // GET /payments/packages returns `price` as a numeric string (e.g.
  // "10.00"), unlike /tiers's `monthlyPriceUsd` which is a number —
  // confirmed against the live dev backend.
  const num = Number(value);
  if (value == null || Number.isNaN(num)) return '—';
  const isInt = Number.isInteger(num);
  return `$${num.toLocaleString('en-US', {
    minimumFractionDigits: isInt ? 0 : 2,
    maximumFractionDigits: 2,
  })}`;
};

/**
 * PackageCard — one purchasable coin package on the /user/coins grid.
 *
 * Props:
 *   pkg:          { id, name?, coinAmount, price } — `price` is a
 *                 numeric string per the live backend response, e.g.
 *                 "10.00".
 *   bonusPercent: number | null — savings vs. the cheapest package's
 *                 price-per-coin, computed by the caller (the backend
 *                 doesn't report a bonus field). Rendered as a "+N%
 *                 bonus" chip when > 0.
 *   featured:     boolean — shows the "Best value" ribbon and a
 *                 highlighted border, for the best price-per-coin
 *                 package.
 *   onBuy:        (pkg) => void — fired by the "Buy" CTA; starts
 *                 the checkout-session call that redirects to Tilopay.
 *   buying:       boolean — true while THIS card's checkout session
 *                 is being created; disables the CTA and shows a
 *                 spinner until the browser redirects away.
 */
export default function PackageCard({
  pkg,
  bonusPercent,
  featured = false,
  onBuy,
  buying = false,
}) {
  const { t } = useTranslation();

  if (!pkg) return null;

  return (
    <article
      className={`package-card${featured ? ' package-card--featured' : ''}`}
    >
      {featured && (
        <span className="package-card__ribbon">
          <Star size={12} aria-hidden="true" />
          {t('walletPage.coinsPurchase.bestValue', 'Best value')}
        </span>
      )}

      <div className="package-card__icon">
        <Coins size={28} aria-hidden="true" />
      </div>

      <div className="package-card__hero">
        <span className="package-card__coins">{formatCoins(pkg.coinAmount)}</span>
      </div>
      <span className="package-card__coins-label">
        {t('walletPage.coinsPurchase.coinsLabel', 'Coins')}
      </span>

      <span className="package-card__bonus">
        {t('walletPage.coinsPurchase.bonusLabel', '+{{percent}}% bonus', {
          percent: bonusPercent > 0 ? bonusPercent : 0,
        })}
      </span>

      <div className="package-card__price">{formatUsd(pkg.price)} USD</div>

      <button
        type="button"
        className="package-card__cta"
        onClick={() => onBuy?.(pkg)}
        disabled={buying}
      >
        {buying ? (
          <>
            <Loader2 size={14} aria-hidden="true" className="package-card__spinner" />
            {t('walletPage.coinsPurchase.redirecting', 'Redirecting…')}
          </>
        ) : (
          t('walletPage.coinsPurchase.buy', 'Buy')
        )}
      </button>
    </article>
  );
}
